import DetailList from "@/shared/ui/DetailList";
import { forbiddenIcon } from "@/shared/ui/alertDialogIcons";
import { formatDate } from "@/shared/lib/format";
import { DIALOG_PRIORITY, type AlertDialogPayload } from "@/shared/model/feedbackStore";

type AccountStatusErrorCode = "USER_WITHDRAWN" | "USER_SUSPENDED" | "USER_BLOCKED";

/**
 * ISO 8601 날짜 문자열을 안전하게 포맷한다.
 * 파싱 실패(잘못된 형식 등)는 null 을 반환해 호출처가 fallback 처리한다.
 */
const safeFormatDate = (value: unknown, pattern = "yyyy.MM.dd"): string | null => {
  if (typeof value !== "string" || !value) return null;
  try {
    return formatDate(value, pattern);
  } catch {
    return null;
  }
};

/**
 * 탈퇴/정지/차단 계정 로그인 차단 팝업 payload 빌더.
 * SNS 로그인·refresh·access 세 경로가 공유하는 DRY 빌더.
 *
 * 동적 안내 규칙:
 * - 날짜 값이 있으면 포맷 후 표시, 없거나 파싱 실패 시 생략 → fallback 문구.
 * - USER_SUSPENDED: endAt=null = 무기한 → "정지 기간: 무기한" 행 항상 표시.
 *                   reason=null 시 "정지 사유" 행 생략.
 * - USER_BLOCKED: 차단된 계정 로그인 안내 → 고정 카피. detail(blockedAt 등)을 노출하지 않는다.
 */
export const accountStatusPopup = (
  errorCode: AccountStatusErrorCode,
  detail: Record<string, unknown> | null | undefined,
): AlertDialogPayload => {
  switch (errorCode) {
    case "USER_WITHDRAWN": {
      const withdrawnAt = safeFormatDate(detail?.withdrawnAt);
      return {
        icon: forbiddenIcon,
        title: "탈퇴된 계정이에요",
        message: withdrawnAt
          ? `${withdrawnAt} 에 탈퇴 처리된 계정이에요.`
          : "탈퇴 처리된 계정이에요.",
        priority: DIALOG_PRIORITY.SESSION,
      };
    }

    case "USER_SUSPENDED": {
      const startAt = safeFormatDate(detail?.startAt);
      // endAt: null = 무기한(확정 의미) → 행 항상 표시. string = 종료일 → 포맷. 파싱 실패 → 무기한 fallback.
      const endAtRaw = detail?.endAt;
      const endAtFormatted =
        typeof endAtRaw === "string" && endAtRaw ? safeFormatDate(endAtRaw) : null;
      const endAtDisplay = endAtFormatted !== null ? `${endAtFormatted} 까지` : "무기한";

      const reason =
        typeof detail?.reason === "string" && detail.reason ? detail.reason : null;

      const items: { label: string; value: string }[] = [];
      if (reason !== null) {
        items.push({ label: "정지 사유", value: reason });
      }
      items.push({ label: "정지 기간", value: endAtDisplay });

      return {
        icon: forbiddenIcon,
        title: "정지된 계정이에요",
        message: startAt
          ? `${startAt} 에 정지 처리된 계정이에요.`
          : "정지된 계정이에요.",
        detail: <DetailList items={items} />,
        priority: DIALOG_PRIORITY.SESSION,
      };
    }

    case "USER_BLOCKED": {
      // (본인인증 연락처 차단 팝업과 문구 구조만 정렬하되, 대상은 '계정'이라 별도 카피)
      return {
        icon: forbiddenIcon,
        title: "차단된 계정이에요",
        message: "운영 정책에 따라 해당 계정은 사용할 수 없어요.",
        priority: DIALOG_PRIORITY.SESSION,
      };
    }
  }
};
